import {
	type ColumnDef,
	type ColumnFiltersState,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getSortedRowModel,
	type SortingState,
	useReactTable,
	type VisibilityState,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { z } from "zod";
import { trackTableFilterChanged } from "../../../analytics";
import { cn } from "../../lib/utils";
import { Icon } from "../../primitives";
import { TableRow } from "../DataTable/TableRow";
import { useTableColumnSizing, useTableSorting } from "../DataTable/utils";
import { FilterableHeaderRow } from "./FilterableHeaderRow";
import { detectFilterChanges } from "./utils";

export function FilterableDataTable<TData extends object, TValue>({
	name,
	columnDefs,
	data,
	loadingMessage,
	columnVisibility,
	defaultColumnFilters,
	initialSorting,
	getRowUrl,
	tableClassName,
	enableExportToCSV,
	minHeight,
	maxHeight,
}: {
	name: string;
	columnDefs: ColumnDef<TData, TValue>[];
	data?: TData[];
	getRowUrl?: (row: TData) => string;
	columnVisibility?: VisibilityState;
	defaultColumnFilters?: ColumnFiltersState;
	initialSorting?: SortingState;
	enableExportToCSV?: boolean;
	loadingMessage?: string;
	tableClassName?: string;
	minHeight?: number;
	maxHeight?: number;
}) {
	const { columnSizing, handleColumnSizingChange } = useTableColumnSizing(
		`inrev_filterable_table_${name}`,
		columnDefs,
	);
	const { sorting, handleSortingChange } = useTableSorting(`${name}_v2_sorting`, initialSorting);

	const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() => {
		try {
			const stored = sessionStorage.getItem(`${name}_v2_filters`);
			if (!stored) return [];
			const result = z
				.array(z.object({ id: z.string(), value: z.unknown() }))
				.safeParse(JSON.parse(stored));
			return result.success ? result.data : [];
		} catch {
			return [];
		}
	});

	const handleColumnFiltersChange = useCallback(
		(updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
			setColumnFilters((prev) => {
				const next = typeof updater === "function" ? updater(prev) : updater;
				sessionStorage.setItem(`${name}_v2_filters`, JSON.stringify(next));
				for (const change of detectFilterChanges(prev, next)) {
					trackTableFilterChanged(name, change.columnId, change.values);
				}
				return next;
			});
		},
		[name],
	);

	const hasAppliedDefault = useRef(false);
	useEffect(() => {
		if (hasAppliedDefault.current || !defaultColumnFilters?.length) return;
		if (columnFilters.length === 0) {
			handleColumnFiltersChange(defaultColumnFilters);
		}
		hasAppliedDefault.current = true;
	}, [defaultColumnFilters, handleColumnFiltersChange]);

	const parentRef = useRef<HTMLDivElement>(null);

	const table = useReactTable<TData>({
		data: data ?? [],
		columns: columnDefs,
		state: {
			sorting,
			columnVisibility,
			columnFilters,
			columnSizing,
		},
		renderFallbackValue: "--",
		onSortingChange: handleSortingChange,
		onColumnFiltersChange: handleColumnFiltersChange,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		onColumnSizingChange: handleColumnSizingChange,
		autoResetAll: false,
	});

	const { rows } = table.getRowModel();

	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 58,
		overscan: 20,
	});

	const { csvData, csvHeaders } = useMemo(() => {
		if (!enableExportToCSV) return { csvData: [], csvHeaders: [] };
		const headers = columnDefs.flatMap((col) => {
			const key =
				col.meta?.csvKey ??
				("accessorKey" in col && typeof col.accessorKey === "string"
					? col.accessorKey
					: undefined) ??
				col.id;
			const label = col.meta?.csvLabel ?? (typeof col.header === "string" ? col.header : undefined);
			if (!key || !label || !col.id) return [];
			return [{ key, label, columnId: col.id, csvFormatter: col.meta?.csvFormatter }];
		});

		const csvRows = rows.map((row) => {
			const obj: Record<string, unknown> = {};
			for (const h of headers) {
				const raw = row.getValue(h.columnId);
				obj[h.key] = h.csvFormatter ? h.csvFormatter(raw) : raw;
			}
			return obj;
		});

		return {
			csvData: csvRows,
			csvHeaders: headers.map(({ key, label }) => ({ key, label })),
		};
	}, [rows, columnDefs, enableExportToCSV]);

	const headerHeight = 33;
	const contentHeight =
		rowVirtualizer.getTotalSize() + headerHeight + ((data ?? []).length > 0 ? 0 : 70);
	const totalHeight = maxHeight ? Math.min(contentHeight, maxHeight) : undefined;
	const containerStyle =
		minHeight && totalHeight
			? { height: Math.max(minHeight, totalHeight) }
			: { height: totalHeight };

	return (
		<div
			className={cn("relative w-full", maxHeight ? undefined : "h-full", tableClassName)}
			style={containerStyle}
		>
			{enableExportToCSV && (
				<div className="absolute top-[-40px] right-0">
					<div className="flex justify-end items-center pb-2">
						<span
							title="Export to CSV"
							className="p-1 rounded-md hover:bg-surface-neutral cursor-pointer"
						>
							<CSVLink
								filename={`data-table-${name}-export.csv`}
								data={csvData}
								headers={csvHeaders}
							>
								<Icon type="document-arrow-down" size="small" color="secondary" />
							</CSVLink>
						</span>
					</div>
				</div>
			)}
			<div className="overflow-hidden h-full w-full relative border border-bc-default rounded-xl">
				<div
					ref={parentRef}
					className="relative h-full w-full overflow-auto bg-surface-light scrollbar-med"
				>
					<div
						className="min-w-full"
						style={{ width: table.getTotalSize(), height: `${rowVirtualizer.getTotalSize()}px` }}
					>
						<FilterableHeaderRow
							headerGroups={table.getHeaderGroups()}
							setColumnSizing={handleColumnSizingChange}
							columnSizing={columnSizing}
							columnSizingInfo={table.getState().columnSizingInfo}
							className="w-full"
						/>
						<div className="relative w-full h-full">
							{rows.length > 0 &&
								rowVirtualizer.getVirtualItems().map((virtualRow) => {
									const row = rows[virtualRow.index];
									return (
										<TableRow
											key={row.id}
											height={virtualRow.size}
											translateY={virtualRow.start}
											row={row}
											getRowUrl={getRowUrl}
										/>
									);
								})}
							{!data && (
								<div className="mt-[32px] w-full text-center text-tertiary italic text-[13px] animate-pulse">
									{loadingMessage ?? "Loading..."}
								</div>
							)}
							{data && rows.length === 0 && (
								<div className="mt-[32px] w-full text-center text-tertiary italic text-[13px]">
									No results found
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
